import { setItem, getItem, clearStorage } from "@utilities/localStorage";
import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import { DefaultValue } from "./settings";
import { AuthContext } from "./types";
import { useQuery } from "@tanstack/react-query";
import Common from "@utilities/common";

interface Props {
    children: ReactNode;
}

const Context = createContext<AuthContext & { profile: IUser }>({
    ...DefaultValue,
    profile: {
        email: "",
        firstName: "",
        lastName: "",
        mobile: "",
        role: "admin",
    },
});

export const AuthProvider: FC<Props> = ({ children }) => {
    const [data, setData] = useState(DefaultValue.values);

    const { data: profileRes } = useQuery(
        ["getUserProfile"],
        () => Common.api.user.GetProfile({ id: data.id }),
        {
            enabled: !!data.token && !!data.id && data.isLogin === "PENDING",
            onSuccess: ({ data: { result } }) => {
                if (!result?.role) {
                    handleLogout();
                    clearStorage();

                    return undefined;
                }

                setData({
                    ...data,
                    isLogin: "TRUE",
                });
            },
            onError: () => {
                handleLogout();
                clearStorage();
            },
        },
    );

    useEffect(() => {
        const token = getItem("token");
        const userId = getItem("userId");

        if (token && userId) {
            setData({
                ...data,
                token,
                id: userId,
            });
        } else {
            setData({
                ...data,
                isLogin: "FALSE",
                token: "",
                id: "",
            });

            clearStorage();
        }
    }, [data.isLogin]);

    const handleLogin: AuthContext["actions"]["handleLogin"] = ({
        token,
        id,
    }) => {
        setItem({
            key: "token",
            value: token,
        });

        setItem({
            key: "userId",
            value: id,
        });

        setData({
            ...data,
            isLogin: "PENDING",
        });
    };

    const handleLogout: AuthContext["actions"]["handleLogout"] = () => {
        setData({
            ...data,
            isLogin: "FALSE",
            token: "",
        });
    };

    return (
        <Context.Provider
            value={{
                values: data,
                actions: {
                    handleLogin,
                    handleLogout,
                },
                profile: profileRes?.data?.result || {
                    email: "",
                    firstName: "",
                    lastName: "",
                    mobile: "",
                    role: "admin",
                },
            }}>
            {children}
        </Context.Provider>
    );
};

export const useAuth = () => useContext(Context);
